/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useNavigate } from '@tanstack/react-router';
import { Tooltip } from 'radix-ui';
import React from 'react';

import { useAccount } from '~/apis';
import { useFirebaseAnalytics } from '~/apis/useFirebase';
import { selectRejoinState, useSelector, useTranslation } from '~/app';
import { config, tutorialConfig } from '~/app/config';
import {
  ROUTE_FREEGAME,
  ROUTE_TABLE,
  ROUTE_TOPLIST,
  ROUTE_TOURNAMENT,
  ROUTE_TRAINING,
} from '~/app/routes';
import { useAuthDispatch, useAuthState } from '~/auth';
import azaspieleLogo from '~/overlay/button-icons/azaspiele.png';
import { ReactComponent as InstructionsIconWhite } from '~/overlay/button-icons/button-instructions-white.svg';
import { ReactComponent as YouTubeIconWhite } from '~/overlay/button-icons/button-youtube-white.svg';
import { ReactComponent as DiscordIconWhite } from '~/overlay/button-icons/discord-white.svg';
import { ReactComponent as WhatsAppIconWhite } from '~/overlay/button-icons/whatsapp-white.svg';
import { cn } from '~/utils/styles';

import RejoinIcon from './RejoinIcon';
import TableIcon from './TableIcon';
import ToplistIcon from './ToplistIcon';

interface HeaderButtonProps {
  onClick: () => void;
  title?: string;
  className?: string;
  icon: React.ReactElement;
  badgeText?: string;
  animated?: boolean;
  /**
   * Priority for responsive text display. Higher priority buttons keep text longer on smaller screens.
   * Priority levels:
   * - 1: High priority (Table, Toplist) - keep text until very small screens
   * - 2: Medium priority (Instructions) - hide text on small screens
   * - 3: Low priority (Rejoin, YouTube, Developer) - icon-only until 2xl (1536px), then full label
   */
  textPriority?: 1 | 2 | 3;
}

export const RejoinButton = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const rejoinState = useSelector(selectRejoinState);
  const { isSuccess } = useAccount();

  if (!isSuccess || rejoinState.type !== 'success') {
    return null;
  }

  return (
    <HeaderButton
      onClick={() => {
        if (rejoinState.mode === 'training') {
          navigate({ to: ROUTE_TRAINING });
        } else if (rejoinState.mode === 'free') {
          navigate({ to: ROUTE_FREEGAME });
        } else if (rejoinState.mode === 'tournament') {
          navigate({ to: ROUTE_TOURNAMENT });
        }
      }}
      title={
        rejoinState.mode === 'training'
          ? t('button_rejoinTraining')
          : rejoinState.mode === 'free'
            ? t('button_rejoinFree')
            : t('button_rejoinTournament')
      }
      icon={<RejoinIcon color="white" />}
      animated
      textPriority={3}
    />
  );
};

export const ToplistButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isAuthorized } = useAuthState();
  const authDispatch = useAuthDispatch();

  return (
    <HeaderButton
      onClick={() => {
        if (isAuthorized) {
          navigate({ to: ROUTE_TOPLIST });
        } else {
          authDispatch({ type: 'login' });
        }
      }}
      title={t('button_toplist')}
      icon={<ToplistIcon color="white" />}
      textPriority={1}
    />
  );
};

export const TableButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isAuthorized } = useAuthState();
  const authDispatch = useAuthDispatch();

  return (
    <HeaderButton
      onClick={() => {
        if (isAuthorized) {
          navigate({ to: ROUTE_TABLE });
        } else {
          authDispatch({ type: 'login' });
        }
      }}
      title={t('button_table')}
      icon={<TableIcon color="white" />}
      textPriority={1}
      // badgeText={t('start_comingSoonBadge')}
    />
  );
};

export const InstructionsButton = () => {
  const { t, locale } = useTranslation();
  const analytics = useFirebaseAnalytics();

  return (
    <HeaderButton
      onClick={() => {
        const pdfUrl =
          tutorialConfig.instructionsPdf[locale as keyof typeof tutorialConfig.instructionsPdf] ||
          tutorialConfig.instructionsPdf.en;
        window.open(pdfUrl, '_blank');
        analytics?.logEvent('open_instructions', {
          event_category: 'tutorial',
          event_label: 'instructions_pdf',
          language: locale,
        });
      }}
      title={t('button_instructions')}
      textPriority={2}
      icon={<InstructionsIconWhite width={24} height={24} />}
    />
  );
};

export const YouTubeButton = () => {
  const { t } = useTranslation();
  const analytics = useFirebaseAnalytics();

  return (
    <HeaderButton
      onClick={() => {
        window.open(tutorialConfig.youtubeChannel, '_blank');
        analytics?.logEvent('open_youtube', {
          event_category: 'tutorial',
          event_label: 'youtube_channel',
        });
      }}
      title={t('button_youtube')}
      textPriority={3}
      icon={<YouTubeIconWhite width={24} height={24} />}
    />
  );
};

export const DiscordButton = () => {
  const { t } = useTranslation();
  const analytics = useFirebaseAnalytics();

  if (!config.discordEnabled || !config.discordInviteUrl) {
    return null;
  }

  return (
    <HeaderButton
      onClick={() => {
        window.open(config.discordInviteUrl, '_blank');
        analytics?.logEvent('open_discord', {
          event_category: 'community',
          event_label: 'discord_community',
        });
      }}
      title={t('button_discord')}
      textPriority={3}
      icon={<DiscordIconWhite width={24} height={24} />}
    />
  );
};

export const WhatsAppButton = () => {
  const { t } = useTranslation();
  const analytics = useFirebaseAnalytics();

  if (!config.whatsappEnabled || !config.whatsappInviteUrl) {
    return null;
  }

  return (
    <HeaderButton
      onClick={() => {
        window.open(config.whatsappInviteUrl, '_blank');
        analytics?.logEvent('open_whatsapp', {
          event_category: 'community',
          event_label: 'whatsapp_community',
        });
      }}
      title={t('button_whatsapp')}
      textPriority={3}
      icon={<WhatsAppIconWhite width={24} height={24} />}
    />
  );
};

export const DeveloperButton = () => {
  const { t, locale } = useTranslation();
  const analytics = useFirebaseAnalytics();

  return (
    <HeaderButton
      onClick={() => {
        const homepageUrl =
          tutorialConfig.creatorHomepage[locale as keyof typeof tutorialConfig.creatorHomepage] ||
          tutorialConfig.creatorHomepage.en;
        window.open(homepageUrl, '_blank');
        analytics?.logEvent('open_developer', {
          event_category: 'tutorial',
          event_label: 'developer_homepage',
          language: locale,
        });
      }}
      title={t('button_developer')}
      textPriority={3}
      icon={
        <span style={{ background: 'white', borderRadius: 4, padding: 2, display: 'inline-block' }}>
          <img
            src={azaspieleLogo}
            alt="AZA-Spiele"
            style={{ height: 20, width: 'auto', display: 'block', objectFit: 'contain' }}
          />
        </span>
      }
    />
  );
};

const HeaderButton = ({
  onClick,
  title,
  className,
  icon,
  badgeText,
  animated,
  textPriority = 1,
}: HeaderButtonProps) => {
  // Determine responsive text classes based on priority
  const getTextClasses = () => {
    switch (textPriority) {
      case 1: // High priority - keep text until very small screens
        return 'lg:inline hidden pl-2 text-sm align-middle';
      case 2: // Medium priority - hide text on small screens
        return 'xl:inline hidden pl-2 text-sm align-middle';
      case 3: // Low priority - hide text on medium screens
        return '2xl:inline hidden pl-2 text-sm align-middle';
      default:
        return 'md:inline hidden pl-2 text-sm align-middle';
    }
  };

  // Show tooltip only when text is hidden (icon-only)
  // We'll use CSS to detect when the text is hidden (using a ref and ResizeObserver is overkill for now)
  // So we always show the tooltip, but it's only useful when text is hidden
  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <div className="relative shrink-0 transform-gpu transition-all hover:scale-[1.02] active:scale-[0.98]">
            <button
              className={cn(
                'my-1 flex h-10 transform-gpu cursor-pointer flex-row items-center justify-center rounded-md bg-primary-light p-2 px-3 transition-all hover:brightness-125 active:brightness-125',
                animated && 'z-0 overflow-hidden',
                animated &&
                  'before:animate-conic-rotate before:absolute before:-top-1/2 before:-left-1/2 before:-z-2 before:h-[200%] before:w-[200%] before:bg-conic',
                animated &&
                  'after:absolute after:top-[2px] after:left-[2px] after:-z-1 after:h-[calc(100%-4px)] after:w-[calc(100%-4px)] after:rounded-md after:bg-primary-light',
                className,
              )}
              onClick={onClick}
              title={title}
              aria-label={title}
            >
              {icon}
              <span className={getTextClasses()}>{title}</span>
            </button>
            {badgeText ? (
              <div className="absolute top-0.5 -left-4 inline-flex origin-center -rotate-12 items-center justify-center rounded-md bg-secondary p-1 shadow-2xl">
                <span className="text-[0.5rem] font-bold whitespace-nowrap text-primary-dark">
                  {badgeText}
                </span>
              </div>
            ) : null}
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={8}
            className="rounded-lg border border-gray-100 bg-white px-2 py-2 text-sm shadow-xl"
          >
            {title}
            <Tooltip.Arrow className="fill-white" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default HeaderButton;
